import {
  AdminSpinner,
  Button,
  Chevron,
  Disclaimer,
  Div,
  H3,
  H5,
  Icon,
  ImagePlaceholderCircle,
  Img,
  Modal,
  ModalFullScreen,
  P,
  Pill,
  PropsModal,
  Separator,
  SuccessToast,
  Tab,
  TabsWrapper,
  centsToDollars,
  convert24HourTo12Hour,
  formatter,
  getMenuPublishHistoryDetail,
  lightTheme,
  noAvailability,
  noItems,
  noModifierGroups,
  renderDayOfWeekString,
  restoreMenuPublishHistory,
  reportError,
} from '@vgrubs/components';
import {
  Availability,
  CategoryV2Response,
  MenuAvailabilityV2Response,
  MenuItemV2Response,
  MenuV2Response,
  ModifierGroupItemV2Response,
  ModifierItemV2Response,
} from 'components/src/types/menu.types';
import { Provider } from 'components/src/types/shared.types';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Collapsible, ItemListImage } from '../MenyStyled';
import { AddItemModal } from '../MenuProviderSectionsPreview/Items/AddItemModal';

export const MenuPublishHistoryPreview = ({
  historyId,
  closeModal,
  setMenuMain,
  handleChangeShouldPublish,
  isPos,
}: PropsModal & {
  historyId: number | null;
  setMenuMain: Dispatch<SetStateAction<MenuV2Response | null>>;
  handleChangeShouldPublish: (shouldPublish: boolean) => void;
  isPos: boolean;
}) => {
  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };

  const [menu, setMenu] = useState<MenuV2Response | null>(null);
  const [loading, setLoading] = useState(false);
  const [menuTab, setMenuTab] = useState(1);
  const [areYouSureRestore, setAreYouSureRestore] = useState(false);
  const [itemDetail, setItemDetail] = useState<{
    item: MenuItemV2Response;
    category: CategoryV2Response;
  } | null>(null);
  const [detailModifierGroups, setDetailModifierGroups] = useState<
    ModifierGroupItemV2Response[]
  >([]);

  useEffect(() => {
    if (historyId) {
      handleGetHistoryDetail(historyId);
    } else {
      setMenu(null);
      setMenuTab(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyId]);

  async function handleGetHistoryDetail(id: number) {
    try {
      setLoading(true);
      const { data } = await getMenuPublishHistoryDetail(
        locationId,
        brandId,
        id
      );
      setMenu(data.data.snapshot);
    } catch (error) {
      reportError(error);
      closeModal?.();
    } finally {
      setLoading(false);
    }
  }

  async function handleRestore() {
    if (!historyId) return;

    try {
      setAreYouSureRestore(false);
      setLoading(true);
      const { data } = await restoreMenuPublishHistory(
        locationId,
        brandId,
        historyId
      );

      setMenuMain(data.data);
      handleChangeShouldPublish(true);
      SuccessToast(data.message || 'Menu successfully restored!');
      setMenu(null);
      closeModal?.();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const RestoreButton = () => (
    <Button $selected $wSM="100%" onClick={() => setAreYouSureRestore(true)}>
      Restore
    </Button>
  );

  const CancelButton = () => (
    <Button $selected $gray $wSM="100%" onClick={() => closeModal?.()}>
      Cancel
    </Button>
  );

  return (
    <>
      {loading && <AdminSpinner />}
      <ModalFullScreen
        height="calc(100vh - 150px)"
        isOpen={!!historyId && !!menu}
        closeModal={() => null}
        header="Publish History Preview"
        exitButton={false}
        $numOfFloatingButtons={isPos ? 1 : 2}
        footer={
          <Div
            $flex
            $justifyEnd
            $gap="20px"
            $columnSM
            $gapSM="16px"
            $widthSM="100%"
          >
            <CancelButton />
            {!isPos && <RestoreButton />}
          </Div>
        }
      >
        <Div>
          <TabsWrapper style={{ paddingTop: 0 }}>
            <Tab selected={menuTab === 1} onClick={() => setMenuTab(1)}>
              Items
            </Tab>
            <Tab selected={menuTab === 2} onClick={() => setMenuTab(2)}>
              Modifiers
            </Tab>
            <Tab selected={menuTab === 3} onClick={() => setMenuTab(3)}>
              Categories
            </Tab>
            <Tab selected={menuTab === 4} onClick={() => setMenuTab(4)}>
              Availability
            </Tab>
          </TabsWrapper>

          {menuTab === 1 && (
            <ItemsReadOnly
              menu={menu}
              onClickItem={(item, category) =>
                setItemDetail({ item, category })
              }
            />
          )}
          {menuTab === 2 && <ModifiersReadOnly menu={menu} />}
          {menuTab === 3 && <CategoriesReadOnly menu={menu} />}
          {menuTab === 4 && <AvailabilityReadOnly menu={menu} />}
        </Div>
      </ModalFullScreen>

      <AddItemModal
        addModal={itemDetail}
        closeModal={() => setItemDetail(null)}
        edit
        readOnly
        setAddModifierGroupModal={() => null}
        menu={menu}
        modifierGroups={detailModifierGroups}
        setModifierGroups={setDetailModifierGroups}
        provider={Provider.gloriafood}
        handleUploadImage={async () => undefined}
      />

      <Modal
        isOpen={areYouSureRestore}
        closeModal={() => setAreYouSureRestore(false)}
        logo={false}
        zIndex={110}
      >
        <H3 $mb="24" $color={lightTheme.primary}>
          Are you sure
        </H3>
        <P $mb="40">
          Are you sure you want to restore this menu? This will replace the
          current menu.
        </P>

        <Div $flex $gap="16px">
          <Button onClick={handleRestore} $w="100%" $selected>
            Restore
          </Button>
          <Button
            $selected
            $gray
            $w="100%"
            onClick={() => setAreYouSureRestore(false)}
          >
            Cancel
          </Button>
        </Div>
      </Modal>
    </>
  );
};

const ItemsReadOnly = ({
  menu,
  onClickItem,
}: {
  menu: MenuV2Response | null;
  onClickItem: (item: MenuItemV2Response, category: CategoryV2Response) => void;
}) => {
  if (!menu || menu.categories.length === 0) {
    return (
      <Div $flex $column $alignCenter>
        <Img alt="" $width="180px" src={noItems} $mb="24" />
        <H3 $mb="12" $center>
          No Items
        </H3>
      </Div>
    );
  }

  return (
    <>
      {menu.categories.map((category) => (
        <ReadOnlyCategoryItems
          key={category.id}
          category={category}
          onClickItem={onClickItem}
        />
      ))}
    </>
  );
};

const ReadOnlyCategoryItems = ({
  category,
  onClickItem,
}: {
  category: CategoryV2Response;
  onClickItem: (item: MenuItemV2Response, category: CategoryV2Response) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (ref.current) setHeight(ref.current.clientHeight);
  }, [category]);

  return (
    <>
      <Div
        $box
        $p="16px 24px"
        $flex
        $alignCenter
        $between
        $mb="8"
        $background={expanded && lightTheme.primary}
        $pointer
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Div $flex $alignCenter $gap="8px">
          <P $bold $m="0" $color={expanded && lightTheme.white}>
            {category.name} ({category.items.length})
          </P>
        </Div>
        <Icon
          $pointer
          $rotate={expanded ? '360deg' : '180deg'}
          color={expanded ? lightTheme.white : lightTheme.darkGray}
        >
          <Chevron />
        </Icon>
      </Div>

      <Collapsible expanded={expanded} elementHeight={height}>
        <div ref={ref}>
          {category.items.map((item) => (
            <ReadOnlyMenuItem
              key={item.id}
              item={item}
              onClick={() => onClickItem(item, category)}
            />
          ))}
          {category.items.length === 0 && (
            <P $m="0" $center>
              No items
            </P>
          )}
        </div>
      </Collapsible>
    </>
  );
};

const ReadOnlyMenuItem = ({
  item,
  onClick,
}: {
  item: MenuItemV2Response;
  onClick: () => void;
}) => {
  const SoldOutPill = () => (
    <Pill $background={lightTheme.primary}>
      <Disclaimer $m="0" $color={lightTheme.white}>
        Out of Stock (Indefinitely)
      </Disclaimer>
    </Pill>
  );

  return (
    <Div
      $box
      $p="12px 24px"
      $flex
      $alignCenter
      $between
      $mb="8"
      $pointer
      onClick={onClick}
    >
      <Div $width="100%" $flex $alignCenter $gap="16px">
        {item.imageUrl ? (
          <ItemListImage>
            <Img alt="" height="50px" src={item.imageUrl} />
          </ItemListImage>
        ) : (
          <ImagePlaceholderCircle />
        )}
        <Div $flex $column $gap="2px">
          <P $mb="0">{item.name}</P>
          <H5 $m="0" $color={lightTheme.primary}>
            {formatter.format(centsToDollars(item.price ?? 0))}
          </H5>
          {item.availability === Availability.SoldOut && (
            <Div $mobile>
              <SoldOutPill />
            </Div>
          )}
        </Div>
      </Div>
      {item.availability === Availability.SoldOut && (
        <Div $desktop>
          <SoldOutPill />
        </Div>
      )}
    </Div>
  );
};

const ModifiersReadOnly = ({ menu }: { menu: MenuV2Response | null }) => {
  if (!menu || menu.modifierGroups.length === 0) {
    return (
      <Div $flex $column $alignCenter>
        <Img alt="" $width="180px" src={noModifierGroups} $mb="24" />
        <H3 $mb="12" $center>
          No Modifier Groups
        </H3>
      </Div>
    );
  }

  return (
    <>
      <H3 $mb="20">
        {menu.modifierGroups.length === 1
          ? 'Modifier Group'
          : 'Modifier Groups'}{' '}
        ({menu.modifierGroups.length})
      </H3>
      {menu.modifierGroups.map((group) => (
        <ReadOnlyModifierGroup key={group.id} group={group} />
      ))}
    </>
  );
};

const ReadOnlyModifierGroup = ({
  group,
}: {
  group: ModifierGroupItemV2Response;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const elementHeight = ref?.current?.clientHeight;
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <Div $box $p="16px 24px" $flex $alignCenter $between $mb="8">
        <Div
          $flex
          $alignCenter
          $gap="8px"
          onClick={() => setExpanded((prev) => !prev)}
          $width="100%"
          $pointer
        >
          <P $bold $m="0">
            {group.name}
          </P>
        </Div>
        <Icon
          $pointer
          $rotate={expanded ? '360deg' : '180deg'}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <Chevron />
        </Icon>
      </Div>
      <Collapsible expanded={expanded} elementHeight={elementHeight}>
        <div ref={ref}>
          {group.options.map((option, i) => (
            <ReadOnlyModifierOption key={i} option={option} />
          ))}
          {group.options.length === 0 && (
            <P $m="0" $center>
              No modifiers
            </P>
          )}
        </div>
      </Collapsible>
    </>
  );
};

const ReadOnlyModifierOption = ({
  option,
}: {
  option: ModifierItemV2Response;
}) => (
  <>
    <Separator />
    <Div $p="16px 20px" $flex $between $alignCenter>
      <P $m="0">{option.name}</P>
      <P $m="0">{formatter.format(centsToDollars(option.price))}</P>
    </Div>
  </>
);

const CategoriesReadOnly = ({ menu }: { menu: MenuV2Response | null }) => {
  if (!menu || menu.categories.length === 0) {
    return (
      <Div $flex $column $alignCenter>
        <Img alt="" $width="180px" src={noItems} $mb="24" />
        <H3 $mb="12" $center>
          No Categories
        </H3>
      </Div>
    );
  }

  return (
    <>
      <H3 $mb="20">
        {menu.categories.length === 1 ? 'Category' : 'Categories'} (
        {menu.categories.length})
      </H3>
      {menu.categories.map((category) => (
        <Div
          key={category.id}
          $box
          $p="12px 24px"
          $flex
          $alignCenter
          $between
          $mb="8"
          $width="100%"
        >
          <Div $width="100%">
            <H5 $m="0" $color={lightTheme.primary}>
              {category.name}
            </H5>
            {category.description && (
              <P $color={lightTheme.lightGray} $mb="0">
                {category.description}
              </P>
            )}
            <P $mb="0">
              {category.items.length}{' '}
              {category.items.length === 1 ? 'item' : 'items'}
            </P>
          </Div>
        </Div>
      ))}
    </>
  );
};

const AvailabilityReadOnly = ({ menu }: { menu: MenuV2Response | null }) => {
  if (!menu || menu.availability.length === 0) {
    return (
      <Div $flex $column $alignCenter>
        <Img alt="" $width="180px" src={noAvailability} $mb="24" />
        <H3 $mb="12" $center>
          No Availability
        </H3>
      </Div>
    );
  }

  return (
    <>
      <H3 $mb="20">Availability ({menu.availability.length})</H3>
      {menu.availability.map((item, i) => (
        <ReadOnlyAvailabilityGroup key={i} item={item} index={i} />
      ))}
    </>
  );
};

const ReadOnlyAvailabilityGroup = ({
  item,
  index,
}: {
  item: MenuAvailabilityV2Response;
  index: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (ref.current) setHeight(ref.current.clientHeight);
  }, [item]);

  return (
    <>
      <Div $box $p="16px 24px" $flex $alignCenter $between $mb="8">
        <Div
          $flex
          $alignCenter
          $gap="8px"
          onClick={() => setExpanded((prev) => !prev)}
          $width="100%"
          $pointer
        >
          <P $bold $m="0">
            {item.name || `Availability ${index + 1}`}
          </P>
        </Div>
        <Icon
          $pointer
          $rotate={expanded ? '360deg' : '180deg'}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <Chevron />
        </Icon>
      </Div>

      <Collapsible expanded={expanded} elementHeight={height}>
        <div ref={ref}>
          {item.hours.map((hour, i) => (
            <div key={i}>
              <Separator />
              <Div $p="16px 20px" $flex $between $alignCenter>
                <div>
                  <Disclaimer $mb="4">
                    {renderDayOfWeekString(hour.day)}
                  </Disclaimer>
                  <P $m="0">
                    {convert24HourTo12Hour(hour.startTime)} -{' '}
                    {convert24HourTo12Hour(hour.endTime)}
                  </P>
                </div>
              </Div>
            </div>
          ))}
          {item.hours.length === 0 && (
            <P $m="0" $center>
              No schedules
            </P>
          )}
        </div>
      </Collapsible>
    </>
  );
};
